import { CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ManualCheckInState } from '../types';
import styles from './ManualCheckInResult.module.css';

interface ManualCheckInResultProps {
	result: ManualCheckInState;
}

/** Right-column verdict for a MANUAL check-in. Built from the just-checked-in row(s) locally, so it states
 * "Checked in!" (never the misleading load-time "not scanned" status the QR card would echo) and carries no
 * "Scan another ticket" button — in manual mode the operator simply picks the next guest from the table. The
 * camera/photo methods still use `PunchupScanResult`; this is the manual-only confirmation. */
export const ManualCheckInResult = ({ result }: ManualCheckInResultProps) => {
	const { t } = useTranslation();

	if (result.type === 'error') {
		return (
			<div className={styles.card}>
				<div className={`${styles.header} ${styles.headerError}`}>
					<XCircle size={48} aria-hidden />
					<h2 className={styles.title}>{t('scanner-manual-failed', 'Check-in failed')}</h2>
				</div>
				<p className={styles.message}>{result.message}</p>
			</div>
		);
	}

	return (
		<div className={styles.card}>
			<div className={`${styles.header} ${styles.headerSuccess}`}>
				<CheckCircle size={48} aria-hidden />
				<h2 className={styles.title}>
					{result.type === 'undone'
						? t('scanner-manual-undone', 'Check-in undone')
						: t('scanner-manual-checked-in', 'Checked in!')}
				</h2>
			</div>
			<div className={styles.body}>
				{result.guestName && <p className={styles.guest}>{result.guestName}</p>}
				{result.email && <p className={styles.email}>{result.email}</p>}
				{result.tickets.length > 0 && (
					<ul className={styles.tickets}>
						{result.tickets.map((label, i) => (
							<li key={`${label}-${i}`} className={styles.ticket}>
								{label}
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};
